import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Inbox, Search, RefreshCw } from "lucide-react";
import DemoRequestRow, { STATUSES } from "@/components/admin/DemoRequestRow";

const FILTERS = ["all", ...STATUSES];

function matches(r, q) {
  if (!q) return true;
  const hay = [r.name, r.company, r.email, r.phone, r.postcode, r.message, r.notes, r.source]
    .join(" ").toLowerCase();
  return hay.includes(q.toLowerCase());
}

export default function DemoRequestsInboxCard() {
  const [data, setData] = useState({ items: [], counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/marketing/demo-requests");
      setData(d);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const onUpdated = (doc) => {
    setData((prev) => {
      const items = prev.items.map((r) => (r.id === doc.id ? doc : r));
      const counts = Object.fromEntries(STATUSES.map((s) => [s, items.filter((r) => r.status === s).length]));
      return { ...prev, items, counts };
    });
  };

  const visible = useMemo(
    () => data.items.filter((r) => (filter === "all" || r.status === filter) && matches(r, q)),
    [data.items, filter, q],
  );

  return (
    <section className="mt-12 border border-zinc-200 rounded-sm bg-white p-6" data-testid="demo-requests-inbox-card">
      <header className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 inline-flex items-center gap-1.5">
            <Inbox className="h-3.5 w-3.5" /> Demo requests
          </p>
          <h2 className="font-heading text-2xl font-bold tracking-tight text-zinc-950 mt-1">Follow up every lead.</h2>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Every "Book a demo" request from your website and the promo video page. Set a status and keep private notes.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} className="h-9 rounded-sm" data-testid="demo-inbox-refresh-btn">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </header>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`demo-inbox-filter-${f}`}
            className={`text-xs font-semibold capitalize px-3 py-1.5 rounded-sm border transition-colors duration-150 ${
              filter === f ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-950"
            }`}>
            {f} <span className="opacity-60 ml-1">{f === "all" ? data.total : data.counts?.[f] || 0}</span>
          </button>
        ))}
        <div className="relative ml-auto w-full sm:w-64">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, pub, email…"
            className="pl-9 h-9 rounded-sm" data-testid="demo-inbox-search" />
        </div>
      </div>

      {loading ? (
        <p className="text-sm uppercase tracking-[0.18em] text-zinc-500">Loading…</p>
      ) : visible.length === 0 ? (
        <p className="text-sm text-zinc-500 border border-dashed border-zinc-200 rounded-sm p-8 text-center" data-testid="demo-inbox-empty">
          No demo requests here yet.
        </p>
      ) : (
        <ul className="divide-y divide-zinc-100 border border-zinc-200 rounded-sm" data-testid="demo-inbox-list">
          {visible.map((r) => <DemoRequestRow key={r.id} req={r} onUpdated={onUpdated} />)}
        </ul>
      )}
    </section>
  );
}
